import { Alert, Button, Flex, Select, Table, Tag, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { formatDays, formatSignedDays } from "../format"
import { fetchMemberMilestones } from "./api"
import type {
  MemberMilestonesResponse,
  MemberQuery,
  MilestoneStat
} from "./types"

const RESULT_COLORS: Record<MilestoneStat["result"], string> = {
  late_pattern: "red",
  watch: "orange",
  good: "green"
}

interface Props {
  memberId: string
  query: MemberQuery
  onMinChange: (min: number) => void
  onOpenMilestone: (row: MilestoneStat) => void
}

export function MilestonesView({
  memberId,
  query,
  onMinChange,
  onOpenMilestone
}: Props) {
  const { t } = useTranslation()
  const [data, setData] = useState<MemberMilestonesResponse | null>(null)
  const [failed, setFailed] = useState(false)
  const key = JSON.stringify([
    query.grain,
    query.from,
    query.to,
    query.projectType,
    query.minOccurrences
  ])

  useEffect(() => {
    let current = true
    setFailed(false)
    fetchMemberMilestones(memberId, query)
      .then((body) => current && setData(body))
      .catch(() => current && setFailed(true))
    return () => {
      current = false
    }
  }, [memberId, key])

  return (
    <Flex vertical gap="middle">
      <Flex gap="small" align="center">
        <Typography.Text type="secondary">
          {t("teamPerformance.milestones.minimum")}
        </Typography.Text>
        <Select<number>
          aria-label={t("teamPerformance.milestones.minimum")}
          value={query.minOccurrences ?? 3}
          onChange={onMinChange}
          options={[1, 3, 5, 10].map((n) => ({ value: n, label: String(n) }))}
        />
      </Flex>
      {failed && (
        <Alert
          type="error"
          showIcon
          message={t("teamPerformance.states.loadFailed")}
        />
      )}
      <Table<MilestoneStat>
        rowKey="key"
        size="middle"
        loading={!data && !failed}
        dataSource={data?.milestones ?? []}
        scroll={{ x: 900 }}
        pagination={false}
        locale={{ emptyText: t("teamPerformance.milestones.empty") }}
        columns={[
          {
            key: "label",
            title: t("teamPerformance.milestones.milestone"),
            fixed: "left",
            render: (_, row) => (
              <Button
                type="link"
                className="px-0"
                onClick={() => onOpenMilestone(row)}
                aria-label={t("teamPerformance.member.viewProjectsOf", {
                  name: row.label
                })}
              >
                {row.label}
              </Button>
            )
          },
          {
            key: "occ",
            title: t("teamPerformance.milestones.occurrences"),
            align: "right",
            render: (_, r) => r.occurrences
          },
          {
            key: "on_time",
            title: t("teamPerformance.table.onTime"),
            align: "right",
            render: (_, r) => `${r.on_time_percent}%`
          },
          {
            key: "avg",
            title: t("teamPerformance.table.avgDelay"),
            align: "right",
            render: (_, r) => formatDays(r.avg_delay_days)
          },
          {
            key: "severe",
            title: t("teamPerformance.milestones.severe"),
            align: "right",
            render: (_, r) => r.severe
          },
          {
            key: "result",
            title: t("teamPerformance.milestones.result"),
            render: (_, r) => (
              <Tag color={RESULT_COLORS[r.result]} bordered={false}>
                {t(`teamPerformance.milestones.results.${r.result}`)}
              </Tag>
            )
          },
          {
            key: "company",
            title: t("teamPerformance.types.company"),
            align: "right",
            render: (_, r) => formatDays(r.company_avg_delay_days)
          },
          {
            key: "diff",
            title: t("teamPerformance.member.difference"),
            align: "right",
            render: (_, r) => formatSignedDays(r.difference_days)
          }
        ]}
      />
    </Flex>
  )
}
